import { supabaseClient } from "@/lib/supabase";
import type { SupportedLocale } from "@patient-watch/i18n";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { resolveDictionary, type DictionaryRow, type OptionRow, type ResolvedDictionary } from "../resolveDictionary";

export interface DictionaryWithOptions {
    dictionary: DictionaryRow;
    options: OptionRow[];
}

export async function selectDictionaryNames(): Promise<string[]> {
    const { data } = await supabaseClient.from("dictionaries").select("name").order("name").throwOnError();
    return data.map((row) => row.name);
}

export async function selectDictionaryByName(name: string): Promise<DictionaryWithOptions> {
    const { data: dictionary } = await supabaseClient
        .from("dictionaries")
        .select("*")
        .eq("name", name)
        .single()
        .throwOnError();
    const { data: options } = await supabaseClient
        .from("options")
        .select("*")
        .eq("dictionary_id", dictionary.dictionary_id)
        .order("sort_order")
        .throwOnError();
    return { dictionary, options };
}

export async function selectDictionaryById(dictionaryId: string): Promise<DictionaryWithOptions> {
    const { data: dictionary } = await supabaseClient
        .from("dictionaries")
        .select("*")
        .eq("dictionary_id", dictionaryId)
        .single()
        .throwOnError();
    const { data: options } = await supabaseClient
        .from("options")
        .select("*")
        .eq("dictionary_id", dictionaryId)
        .order("sort_order")
        .throwOnError();
    return { dictionary, options };
}

export const dictionaryByIdQueryOptions = (dictionaryId: string) =>
    queryOptions({
        queryKey: ["dictionary-by-id", dictionaryId],
        queryFn: () => selectDictionaryById(dictionaryId),
        staleTime: 5 * 60 * 1000,
    });

export interface DictionaryListItem {
    dictionary_id: string;
    name: string;
}

export const dictionaryListQueryOptions = () =>
    queryOptions({
        queryKey: ["dictionary-list"],
        queryFn: async (): Promise<DictionaryListItem[]> => {
            const { data } = await supabaseClient
                .from("dictionaries")
                .select("dictionary_id, name")
                .order("name")
                .throwOnError();
            return data;
        },
        staleTime: 5 * 60 * 1000,
    });

export const dictionaryNamesQueryOptions = () =>
    queryOptions({
        queryKey: ["dictionary-names"],
        queryFn: selectDictionaryNames,
    });

/** Raw rows are cached per name. The locale is applied on the client, so switching language does not refetch. */
export const dictionaryQueryOptions = (name: string) =>
    queryOptions({
        queryKey: ["dictionary", name],
        queryFn: () => selectDictionaryByName(name),
    });

export function useDictionaryNames() {
    return useQuery(dictionaryNamesQueryOptions());
}

export function useDictionary(name: string, locale: SupportedLocale) {
    const query = useQuery(dictionaryQueryOptions(name));
    const resolved: ResolvedDictionary | null = useMemo(
        () => (query.data ? resolveDictionary(query.data.dictionary, query.data.options, locale) : null),
        [query.data, locale]
    );
    return { ...query, resolved };
}
